import { LoadingIndicator } from '@commenda/kakashi';
import { useEffect, useMemo, useRef } from 'react';
import { CalendarPreviewHelper } from '../../helpers';
import { FilingCalendarPreviewContentProps } from '../../types';
import { CalendarPreviewTable } from '../CalendarPreviewTable/CalendarPreviewTable';

export function FilingCalendarPreviewContent({
  previewData,
  isLoading,
  taxPeriodStart,
  taxPeriodEnd,
  showProposedCalendar = false,
  onFilingDeleted,
  corporationId,
}: FilingCalendarPreviewContentProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);

  const years = useMemo(() => {
    const yearsFromData = previewData
      ? CalendarPreviewHelper.getYearRangeFromPreviewData({
          ...previewData,
          final_calendar:
            showProposedCalendar && previewData.final_calendar.length > 0
              ? previewData.final_calendar
              : previewData.existing_filings,
        })
      : [new Date().getFullYear()];

    const allYears = new Set(yearsFromData);
    if (taxPeriodStart) {
      allYears.add(taxPeriodStart.year);
    }
    if (taxPeriodEnd) {
      allYears.add(taxPeriodEnd.year);
    }

    return Array.from(allYears).sort((a, b) => a - b);
  }, [previewData, showProposedCalendar, taxPeriodStart, taxPeriodEnd]);

  const months = useMemo(() => {
    return CalendarPreviewHelper.generateMonthsForYears(years);
  }, [years]);

  useEffect(() => {
    if (!taxPeriodStart || !scrollContainerRef.current || isLoading) {
      return;
    }

    // Don't scroll if months haven't been generated yet
    if (months.length === 0) {
      return;
    }

    // Verify the month exists in the months array
    const monthExists = months.some(
      (m) =>
        m.year === taxPeriodStart.year && m.index + 1 === taxPeriodStart.month
    );

    if (!monthExists) {
      return;
    }

    hasScrolledRef.current = false;

    const scrollToStartMonth = () => {
      const container = scrollContainerRef.current;
      if (!container || hasScrolledRef.current) return;

      const selector = `[data-month="${taxPeriodStart.year}-${taxPeriodStart.month}"]`;
      // querySelector returns Element | null; we render [data-month] nodes as
      // divs above, so the generic parameter narrows to HTMLElement safely.
      const monthElement = container.querySelector<HTMLElement>(selector);

      if (monthElement) {
        hasScrolledRef.current = true;

        const containerRect = container.getBoundingClientRect();
        const elementRect = monthElement.getBoundingClientRect();

        const currentScrollTop = container.scrollTop;
        const elementTopRelativeToContainer =
          elementRect.top - containerRect.top + currentScrollTop;

        const headerOffset = 240;
        const scrollPosition = elementTopRelativeToContainer - headerOffset;

        container.scrollTo({
          top: Math.max(0, scrollPosition),
          behavior: 'smooth',
        });
      } else {
        // If element not found, try again after a short delay
        setTimeout(() => {
          // Same HTMLElement narrowing as above; repeated in the retry path.
          const retryElement = container.querySelector<HTMLElement>(selector);
          if (retryElement && !hasScrolledRef.current) {
            hasScrolledRef.current = true;
            const containerRect = container.getBoundingClientRect();
            const elementRect = retryElement.getBoundingClientRect();
            const currentScrollTop = container.scrollTop;
            const elementTopRelativeToContainer =
              elementRect.top - containerRect.top + currentScrollTop;
            const headerOffset = 240;
            const scrollPosition = elementTopRelativeToContainer - headerOffset;

            container.scrollTo({
              top: Math.max(0, scrollPosition),
              behavior: 'smooth',
            });
          } else if (!retryElement) {
            // do nothing
          }
        }, 200);
      }
    };

    // Wait for next frame to ensure rendering is complete
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(scrollToStartMonth, 100);
      });
    });
  }, [taxPeriodStart, isLoading, months]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <LoadingIndicator size={32} />
      </div>
    );
  }

  const hasFilings =
    previewData &&
    (previewData.existing_filings.length > 0 ||
      (showProposedCalendar && previewData.final_calendar.length > 0));

  if (!hasFilings) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center p-4xl">
        <p className="txt-medium-regular text-text-tertiary">
          Configure the filing calendar settings on the left and click Preview
          to see the calendar.
        </p>
      </div>
    );
  }

  const proposedFilingCalendar = !previewData.can_generate
    ? []
    : showProposedCalendar && previewData.final_calendar.length > 0
      ? previewData.final_calendar
      : [];

  return (
    <div className="flex flex-col h-full">
      <div ref={scrollContainerRef} className="flex-1 overflow-auto">
        <CalendarPreviewTable
          months={months}
          newCalendarFilings={proposedFilingCalendar}
          oldCalendarFilings={
            previewData.existing_filings.length > 0
              ? previewData.existing_filings
              : undefined
          }
          showOldCalendar={previewData.existing_filings.length > 0}
          coverageGaps={previewData.coverage_gaps}
          taxPeriodStart={taxPeriodStart}
          taxPeriodEnd={taxPeriodEnd}
          onFilingDeleted={onFilingDeleted}
          corporationId={corporationId}
        />
      </div>
    </div>
  );
}
